import { z } from 'zod';
import {
  ChatConnectLinkCreateResponseSchema,
  IntegrationCompaniesResponseSchema,
  IntegrationPendingResponseSchema,
  type ChatConnectLinkCreateResponse,
  type ChatProvider,
  type IntegrationCompany,
  type IntegrationPendingResponse,
} from '@voll-intelligence/domain';
import type { HttpService } from '@web/data/services';

const StatusResponseSchema = z.object({ status: z.string() });

export type {
  ChatConnectionState,
  ChatConnectLinkCreateResponse,
  ChatProvider,
  IntegrationCompany,
  IntegrationPendingResponse,
} from '@voll-intelligence/domain';

interface AdminIntegrationsRepository {
  /** By name (group or company); without `q`, the groups that have a tool or an open link. */
  listCompanies: (orgId: string, q?: string) => Promise<IntegrationCompany[]>;
  listPending: (orgId: string) => Promise<IntegrationPendingResponse>;
  /** The URL comes back once: the connector keeps only a hash of the token. */
  issueLink: (
    orgId: string,
    provider: ChatProvider,
    groupId: string,
  ) => Promise<ChatConnectLinkCreateResponse>;
  cancelLink: (orgId: string, provider: ChatProvider, groupId: string) => Promise<void>;
  confirmReview: (orgId: string, provider: ChatProvider, groupId: string) => Promise<void>;
  rejectReview: (orgId: string, provider: ChatProvider, groupId: string) => Promise<void>;
  disconnect: (orgId: string, provider: ChatProvider, groupId: string) => Promise<void>;
  /** Links an account installed with no company (Slack's public install) to a group. */
  linkAccount: (
    orgId: string,
    provider: ChatProvider,
    accountId: string,
    groupId: string,
  ) => Promise<void>;
}

/**
 * Wraps kanban-api's `/orgs/:orgId/integrations` surface (Cognito user JWT; the
 * server checks `kanban:integrations:*` per action). Contracts come from
 * `@voll-intelligence/domain`.
 */
export function createAdminIntegrationsRepository(http: HttpService): AdminIntegrationsRepository {
  const base = (orgId: string) => `/orgs/${encodeURIComponent(orgId)}/integrations`;
  const tool = (orgId: string, provider: ChatProvider, groupId: string) =>
    `${base(orgId)}/companies/${encodeURIComponent(groupId)}/${provider}`;
  const settle = async (call: Promise<unknown>) => {
    await call;
  };
  const opts = { schema: StatusResponseSchema };
  return {
    async listCompanies(orgId, q) {
      const { data } = await http.get(`${base(orgId)}/companies`, {
        query: { q: q?.trim() ? q.trim() : undefined },
        schema: IntegrationCompaniesResponseSchema,
      });
      return data;
    },
    listPending: (orgId) =>
      http.get(`${base(orgId)}/pending`, { schema: IntegrationPendingResponseSchema }),
    issueLink: (orgId, provider, groupId) =>
      http.post(`${tool(orgId, provider, groupId)}/link`, undefined, {
        schema: ChatConnectLinkCreateResponseSchema,
      }),
    cancelLink: (orgId, provider, groupId) =>
      settle(http.delete(`${tool(orgId, provider, groupId)}/link`, opts)),
    confirmReview: (orgId, provider, groupId) =>
      settle(http.post(`${tool(orgId, provider, groupId)}/review/confirm`, undefined, opts)),
    rejectReview: (orgId, provider, groupId) =>
      settle(http.post(`${tool(orgId, provider, groupId)}/review/reject`, undefined, opts)),
    disconnect: (orgId, provider, groupId) =>
      settle(http.delete(tool(orgId, provider, groupId), opts)),
    linkAccount: (orgId, provider, accountId, groupId) =>
      settle(
        http.post(
          `${base(orgId)}/accounts/${provider}/${encodeURIComponent(accountId)}/link`,
          { corporateGroupId: groupId },
          opts,
        ),
      ),
  };
}
